import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActiveUserService } from 'src/app/services/active-user.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DataFetchService } from 'src/app/services/data-fetch.service';
import { DataGridColumn } from 'src/app/utils/interfaces';
import { Utils } from 'src/app/utils/utils';
import { UserInfo } from '@memberjunction/core';
import { PortalAccessLogEntity, ExtendedUserEntity } from 'mj_generatedentities';
import { EntityNames } from '../../utils/constants';
import { UserEntity } from '@memberjunction/core-entities';
import { UAParser } from 'ua-parser-js';

/**
 * Portal Access Log entry for display
 */
interface PortalAccessLogDisplay {
  id: string;
  userId: string;
  userName: string;
  email: string;
  personId: string | null;
  auraUniqueId: string | null;
  userRole: string | null;
  eventType: string;
  timestamp: Date;
  clientIP: string | null;
  userAgent: string | null;
  browserInfo: string;
  osInfo: string;
  sessionDuration: number | null;
  impersonationSessionId: string | null;
  oktaTokenClaims: string | null;
  sessionStatus: 'Active' | 'Ended' | 'Expired' | 'Unknown';
  ipWarning: boolean;
}

/**
 * Date range preset
 */
interface DateRangePreset {
  label: string;
  days: number;
}

/**
 * IP Address statistics for a user
 */
interface IPAddressStats {
  userId: string;
  ipAddresses: Set<string>;
  hasMultipleIPs: boolean;
}

/**
 * Component for viewing portal access logs
 * Provides audit trail of user logins, logouts, and session expiry events
 * Shows comprehensive Okta authentication data and session information
 */
@Component({
  selector: 'app-portal-access-logs',
  templateUrl: './portal-access-logs.html',
  styleUrls: ['./portal-access-logs.scss']
})
export class PortalAccessLogsComponent implements OnInit, OnDestroy {
  // Access logs (all logs from database)
  allAccessLogs: PortalAccessLogDisplay[] = [];

  // Filtered access logs (displayed in grid)
  accessLogs: PortalAccessLogDisplay[] = [];

  loadingLogs: boolean = false;

  // Current user
  currentUser: UserInfo | null = null;

  // Date range filtering
  dateRangePresets: DateRangePreset[] = [
    { label: 'Today', days: 0 },
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'Last 90 Days', days: 90 }
  ];

  selectedDateRange: number = 30; // Default to last 30 days
  customStartDate: string = '';
  customEndDate: string = '';
  useCustomDateRange: boolean = false;

  // Active user filter for drill-down
  selectedUserFilter: string | null = null;

  // Advanced filters
  showAdvancedFilters: boolean = false;
  filterByImpersonation: 'all' | 'impersonation' | 'normal' = 'all';
  filterBySessionStatus: 'all' | 'Active' | 'Ended' | 'Expired' = 'all';
  filterByIPWarning: boolean = false;

  // IP Address insights
  ipAddressStats: Map<string, IPAddressStats> = new Map();

  // Selected log for claims modal
  selectedLog: PortalAccessLogDisplay | null = null;
  showClaimsModal: boolean = false;

  // Grid columns for access logs
  accessLogColumns: DataGridColumn[] = [
    {
      key: 'timestamp',
      label: 'Date/Time',
      type: 'date',
      sortable: true,
      filterable: false,
      width: '180px',
      align: 'left',
      format: (value: Date) => this.formatTimestamp(value)
    },
    {
      key: 'eventType',
      label: 'Event',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '150px',
      align: 'center',
      filterOptions: [
        { raw: 'Login', formatted: 'Login' },
        { raw: 'Logout', formatted: 'Logout' },
        { raw: 'SessionExpired', formatted: 'Session Expired' }
      ],
      format: (value: string) => this.getEventBadge(value)
    },
    {
      key: 'userName',
      label: 'User Name',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '200px',
      align: 'left'
    },
    {
      key: 'email',
      label: 'Email',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '220px',
      align: 'left'
    },
    {
      key: 'userRole',
      label: 'Role',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '150px',
      align: 'left'
    },
    {
      key: 'browserInfo',
      label: 'Browser',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '150px',
      align: 'left'
    },
    {
      key: 'osInfo',
      label: 'OS',
      type: 'text',
      sortable: true,
      filterable: true,
      dropdownFilter: true,
      width: '130px',
      align: 'left'
    },
    {
      key: 'clientIP',
      label: 'IP Address',
      type: 'text',
      sortable: true,
      filterable: true,
      width: '160px',
      align: 'left',
      format: (value: string | null) => value || 'N/A'
    },
    {
      key: 'personId',
      label: 'Person ID',
      type: 'text',
      sortable: true,
      filterable: true,
      width: '120px',
      align: 'left',
      format: (value: string | null) => value || 'N/A'
    },
    {
      key: 'sessionDuration',
      label: 'Duration',
      type: 'text',
      sortable: true,
      filterable: false,
      width: '110px',
      align: 'center',
      format: (value: number | null) => value ? this.formatDuration(value) : 'N/A'
    },
    {
      key: 'impersonationSessionId',
      label: 'Impersonation',
      type: 'text',
      sortable: true,
      filterable: false,
      width: '150px',
      align: 'center',
      format: (value: string | null) => value ? '🔐 Yes' : '-'
    },
    {
      key: 'actions',
      label: 'Actions',
      type: 'actions',
      sortable: false,
      filterable: false,
      width: '100px',
      align: 'center',
      actions: [
        {
          icon: '🔍',
          label: 'View Details',
          handler: (item: PortalAccessLogDisplay) => this.viewClaims(item)
        }
      ]
    }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private activeUserService: ActiveUserService,
    private notificationService: NotificationService,
    private dataFetchService: DataFetchService,
    public utils: Utils
  ) {}

  async ngOnInit(): Promise<void> {
    // Get current user
    this.activeUserService.activeUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(userInfo => {
        if (userInfo) {
          this.currentUser = userInfo.activeUser;
        }
      });

    // Initialize custom date range to last 30 days
    this.initializeCustomDateRange();

    // Load initial data
    await this.loadAccessLogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize custom date range to match selected preset
   */
  initializeCustomDateRange(): void {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - this.selectedDateRange);

    this.customEndDate = this.formatDateForInput(endDate);
    this.customStartDate = this.formatDateForInput(startDate);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Handle date range preset selection
   */
  selectDateRange(days: number): void {
    this.selectedDateRange = days;
    this.useCustomDateRange = false;
    this.initializeCustomDateRange();
    this.applyFilters();
  }

  /**
   * Handle custom date range change
   */
  onCustomDateRangeChange(): void {
    this.useCustomDateRange = true;
    this.applyFilters();
  }

  /**
   * Get date filter for database query
   */
  getDateFilter(): string {
    let startDate: Date;
    let endDate: Date = new Date();

    if (this.useCustomDateRange && this.customStartDate && this.customEndDate) {
      startDate = new Date(this.customStartDate);
      endDate = new Date(this.customEndDate);
      endDate.setHours(23, 59, 59, 999); // End of day
    } else {
      if (this.selectedDateRange === 0) {
        // Today only
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
      } else {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - this.selectedDateRange);
        startDate.setHours(0, 0, 0, 0);
      }
    }

    const startDateStr = startDate.toISOString();
    const endDateStr = endDate.toISOString();

    return `__mj_CreatedAt >= '${startDateStr}' AND __mj_CreatedAt <= '${endDateStr}'`;
  }

  /**
   * Loads all portal access logs with date filtering
   */
  async loadAccessLogs(): Promise<void> {
    this.loadingLogs = true;
    try {
      const dateFilter = this.getDateFilter();

      // Use parallel queries to get access logs, users, and extended users (for roles)
      const [logsResult, usersResult, extendedUsersResult] = await this.dataFetchService.fetchMultipleEntities([
        {
          EntityName: EntityNames.PortalAccessLogs,
          ExtraFilter: dateFilter,
          OrderBy: '__mj_CreatedAt DESC',
          ResultType: 'simple'
        },
        {
          EntityName: EntityNames.Users,
          ResultType: 'simple'
        },
        {
          EntityName: EntityNames.ExtendedUsers,
          ResultType: 'simple'
        }
      ]);

      if (!logsResult.success || !usersResult.success) {
        console.error('Error fetching access logs or users');
        this.notificationService.error(
          'Error Loading Logs',
          'Failed to load portal access logs. Please try again.',
          5000
        );
        return;
      }

      const logs = logsResult.data as PortalAccessLogEntity[];
      const users = usersResult.data as UserEntity[];
      const extendedUsers = extendedUsersResult.success ? extendedUsersResult.data : [];

      // Create user lookup map for performance
      const userMap = new Map();
      users.forEach(user => userMap.set(user.ID, user.FirstLast));

      // Create role lookup map from ExtendedUsers
      const roleMap = new Map<string, string>();
      if (extendedUsers && Array.isArray(extendedUsers)) {
        (extendedUsers as ExtendedUserEntity[]).forEach((extUser: ExtendedUserEntity) => {
          // ExtendedUsersEntity has User_ID and RoleName properties
          if (extUser.User_ID && extUser.Role) {
            roleMap.set(extUser.User_ID, extUser.Role);
          }
        });
      }

      // Map logs to display format with user agent parsing
      this.allAccessLogs = logs.map(log => this.mapLogToDisplay(log, userMap, roleMap));

      // Calculate IP address statistics
      this.calculateIPAddressStats();

      // Calculate session status
      this.calculateSessionStatus();

      // Apply filters
      this.applyFilters();
    } catch (error) {
      console.error('Error loading access logs:', error);
      this.notificationService.error(
        'Error Loading Logs',
        'Failed to load portal access logs. Please try again.',
        5000
      );
    } finally {
      this.loadingLogs = false;
    }
  }

  /**
   * Map log entity to display format with user agent parsing
   */
  private mapLogToDisplay(log: PortalAccessLogEntity, userMap: Map<string, string>, roleMap: Map<string, string>): PortalAccessLogDisplay {
    const parser = new UAParser(log.UserAgent || undefined);
    const browser = parser.getBrowser();
    const os = parser.getOS();

    const browserInfo = browser.name
      ? `${browser.name}${browser.version ? ' ' + browser.version : ''}`
      : 'Unknown';

    const osInfo = os.name
      ? `${os.name}${os.version ? ' ' + os.version : ''}`
      : 'Unknown';

    // Handle potentially null User_ID defensively
    const userId = log.User_ID || 'unknown';

    // Build userName with multiple fallbacks - prioritize FullName from database
    let userName = 'Unknown User';
    if (log.FullName && log.FullName.trim()) {
      // FullName from database is the primary source
      userName = log.FullName;
    } else if (log.User_ID) {
      // Fallback to user map lookup
      const mappedName = userMap.get(log.User_ID);
      if (mappedName && mappedName.trim()) {
        userName = mappedName;
      } else if (log.Email && log.Email.trim()) {
        userName = log.Email;
      }
    } else if (log.Email && log.Email.trim()) {
      userName = log.Email;
    }

    // Get role from ExtendedUsers lookup, with fallbacks
    let userRole: string | null = null;
    if (log.User_ID && roleMap.has(log.User_ID)) {
      userRole = roleMap.get(log.User_ID) || null;
    } else if (log.UserRole && log.UserRole.trim()) {
      // Fallback to role stored in log record
      userRole = log.UserRole;
    }

    return {
      id: log.ID,
      userId: userId,
      userName: userName,
      email: log.Email && log.Email.trim() ? log.Email : 'N/A',
      personId: log.PersonID || null,
      auraUniqueId: log.AuraUniqueID || null,
      userRole: userRole,
      eventType: log.EventType || 'Unknown',
      timestamp: new Date(log.__mj_CreatedAt),
      clientIP: log.ClientIP && log.ClientIP.trim() ? log.ClientIP : null,
      userAgent: log.UserAgent || null,
      browserInfo,
      osInfo,
      sessionDuration: log.SessionDuration,
      impersonationSessionId: log.ImpersonationSessionID || null,
      oktaTokenClaims: log.OktaTokenClaims || null,
      sessionStatus: 'Unknown' as 'Unknown',
      ipWarning: false
    };
  }

  /**
   * Calculate IP address statistics for all users
   */
  private calculateIPAddressStats(): void {
    this.ipAddressStats.clear();

    this.allAccessLogs.forEach(log => {
      if (!log.clientIP) return;

      if (!this.ipAddressStats.has(log.userId)) {
        this.ipAddressStats.set(log.userId, {
          userId: log.userId,
          ipAddresses: new Set(),
          hasMultipleIPs: false
        });
      }

      const stats = this.ipAddressStats.get(log.userId)!;
      stats.ipAddresses.add(log.clientIP);
      stats.hasMultipleIPs = stats.ipAddresses.size > 1;
    });

    // Mark logs with IP warnings
    this.allAccessLogs.forEach(log => {
      const stats = this.ipAddressStats.get(log.userId);
      log.ipWarning = stats?.hasMultipleIPs || false;
    });
  }

  /**
   * Calculate session status for all logs
   */
  private calculateSessionStatus(): void {
    // Group logs by user
    const userLogs = new Map<string, PortalAccessLogDisplay[]>();
    this.allAccessLogs.forEach(log => {
      if (!userLogs.has(log.userId)) {
        userLogs.set(log.userId, []);
      }
      userLogs.get(log.userId)!.push(log);
    });

    // For each user, determine session status
    userLogs.forEach(logs => {
      // Sort by timestamp
      logs.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

      // Track login events that need matching logout/expiry
      const openLogins: PortalAccessLogDisplay[] = [];

      logs.forEach(log => {
        if (log.eventType === 'Login') {
          openLogins.push(log);
        } else if (log.eventType === 'Logout') {
          // Match with most recent login
          if (openLogins.length > 0) {
            const matchedLogin = openLogins.pop()!;
            matchedLogin.sessionStatus = 'Ended';
          }
          log.sessionStatus = 'Ended';
        } else if (log.eventType === 'SessionExpired') {
          // Match with most recent login
          if (openLogins.length > 0) {
            const matchedLogin = openLogins.pop()!;
            matchedLogin.sessionStatus = 'Expired';
          }
          log.sessionStatus = 'Expired';
        }
      });

      // Remaining open logins are active
      openLogins.forEach(log => {
        log.sessionStatus = 'Active';
      });
    });
  }

  /**
   * Apply all filters to the logs
   */
  applyFilters(): void {
    let filtered = [...this.allAccessLogs];

    // Apply user filter (drill-down)
    if (this.selectedUserFilter) {
      filtered = filtered.filter(log => log.userId === this.selectedUserFilter);
    }

    // Apply impersonation filter
    if (this.filterByImpersonation === 'impersonation') {
      filtered = filtered.filter(log => log.impersonationSessionId !== null);
    } else if (this.filterByImpersonation === 'normal') {
      filtered = filtered.filter(log => log.impersonationSessionId === null);
    }

    // Apply session status filter
    if (this.filterBySessionStatus !== 'all') {
      filtered = filtered.filter(log => log.sessionStatus === this.filterBySessionStatus);
    }

    // Apply IP warning filter
    if (this.filterByIPWarning) {
      filtered = filtered.filter(log => log.ipWarning);
    }

    this.accessLogs = filtered;
  }

  /**
   * Toggle advanced filters panel
   */
  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  /**
   * Handle filter change
   */
  onFilterChange(): void {
    this.applyFilters();
  }

  /**
   * Filter by specific user (drill-down)
   */
  filterByUser(userId: string): void {
    this.selectedUserFilter = userId;
    this.applyFilters();

    const user = this.allAccessLogs.find(log => log.userId === userId);
    if (user) {
      this.notificationService.success(
        'Filter Applied',
        `Showing all access logs for ${user.userName}`,
        3000
      );
    }
  }

  /**
   * Clear user filter
   */
  clearUserFilter(): void {
    this.selectedUserFilter = null;
    this.applyFilters();
    this.notificationService.success(
      'Filter Cleared',
      'Showing all users',
      2000
    );
  }

  /**
   * Clear all advanced filters
   */
  clearAdvancedFilters(): void {
    this.filterByImpersonation = 'all';
    this.filterBySessionStatus = 'all';
    this.filterByIPWarning = false;
    this.selectedUserFilter = null;
    this.applyFilters();
  }

  /**
   * Format user name with drill-down link
   */
  formatUserName(log: PortalAccessLogDisplay | null): string {
    if (!log) return '<span style="color: var(--acs-text-secondary); font-style: italic;">Unknown User</span>';

    // Ensure we always have a display name
    const displayName = log.userName && log.userName.trim() ? log.userName : 'Unknown User';

    return `<span class="user-name-link" data-user-id="${log.userId}">${displayName}</span>`;
  }

  /**
   * Format IP address with warning indicator
   */
  formatIPAddress(log: PortalAccessLogDisplay | null): string {
    // Handle null log or null/empty clientIP
    if (!log || !log.clientIP || !log.clientIP.trim()) {
      return '<span style="color: var(--acs-text-secondary); font-style: italic;">N/A</span>';
    }

    if (log.ipWarning) {
      return `<span class="ip-warning" title="User has logged in from multiple IP addresses">⚠️ ${log.clientIP}</span>`;
    }

    return log.clientIP;
  }

  /**
   * View Okta claims for a log entry
   */
  viewClaims(log: PortalAccessLogDisplay): void {
    this.selectedLog = log;
    this.showClaimsModal = true;
  }

  /**
   * Close claims modal
   */
  closeClaimsModal(): void {
    this.showClaimsModal = false;
    this.selectedLog = null;
  }

  /**
   * Gets event badge HTML for display
   */
  getEventBadge(eventType: string): string {
    const eventMap: { [key: string]: string } = {
      'Login': '✅ Login',
      'Logout': '🚪 Logout',
      'SessionExpired': '⏳ Expired'
    };
    return eventMap[eventType] || eventType;
  }

  /**
   * Gets session status badge HTML for display
   */
  getSessionStatusBadge(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Active': '🟢 Active',
      'Ended': '✅ Ended',
      'Expired': '⏳ Expired',
      'Unknown': '❓ Unknown'
    };
    return statusMap[status] || status;
  }

  /**
   * Formats duration in seconds to human-readable string
   */
  formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Formats timestamp for display (date + time)
   */
  formatTimestamp(date: Date): string {
    if (!date) return '';

    try {
      const d = new Date(date);
      const dateStr = this.utils.formatDate(d.toISOString());
      const timeStr = d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      return `${dateStr} ${timeStr}`;
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return '';
    }
  }

  /**
   * Parse Okta claims JSON into structured data
   */
  parseOktaClaims(claimsJson: string | null): any {
    if (!claimsJson) {
      return null;
    }

    try {
      return JSON.parse(claimsJson);
    } catch (error) {
      console.error('Error parsing Okta claims:', error);
      return null;
    }
  }

  /**
   * Get filtered user name for display
   */
  getFilteredUserName(): string {
    if (!this.selectedUserFilter) return '';
    const user = this.allAccessLogs.find(log => log.userId === this.selectedUserFilter);
    return user?.userName || 'Unknown User';
  }

  /**
   * Get active sessions count
   */
  getActiveSessionsCount(): number {
    return this.allAccessLogs.filter(log => log.sessionStatus === 'Active').length;
  }

  /**
   * Get unique users count
   */
  getUniqueUsersCount(): number {
    const uniqueUsers = new Set(this.allAccessLogs.map(log => log.userId));
    return uniqueUsers.size;
  }

  /**
   * Handle cell click events from data grid
   */
  onCellClick(event: any): void {
    if (event.column?.key === 'userName' && event.row?.userId) {
      this.filterByUser(event.row.userId);
    }
  }
}
